import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import React, { useEffect, useRef } from 'react';
/**
 * Mounts inside `LexicalComposer` and reports genuine user edits of the draft.
 * Only native DOM input on the contenteditable fires these events; the
 * composer's programmatic `editor.update()` mutations (for example dictation
 * appends) do not, so consumers can treat every call as a real user edit.
 *
 * Mirrors the legacy Ask input: `beforeinput` covers typing and IME commits,
 * paste is an explicit signal because Lexical's paste command calls
 * `preventDefault()` (suppressing the native `beforeinput`), and Backspace /
 * Delete are intercepted on `keydown` because Lexical's deletion command
 * handlers `preventDefault()` the keydown and suppress the follow-up
 * `beforeinput`.
 */
export const UserEditPlugin = React.memo(function UserEditPlugin({ onUserEdit }) {
    const [editor] = useLexicalComposerContext();
    const onUserEditRef = useRef(onUserEdit);
    onUserEditRef.current = onUserEdit;
    useEffect(() => {
        const handleBeforeInput = () => onUserEditRef.current();
        const handlePaste = () => onUserEditRef.current();
        const handleKeyDown = (event) => {
            if (event.key === 'Backspace' || event.key === 'Delete') {
                onUserEditRef.current();
            }
        };
        return editor.registerRootListener((rootEl, prevEl) => {
            if (prevEl) {
                prevEl.removeEventListener('beforeinput', handleBeforeInput);
                prevEl.removeEventListener('paste', handlePaste);
                prevEl.removeEventListener('keydown', handleKeyDown);
            }
            if (rootEl) {
                rootEl.addEventListener('beforeinput', handleBeforeInput);
                rootEl.addEventListener('paste', handlePaste);
                rootEl.addEventListener('keydown', handleKeyDown);
            }
        });
    }, [editor]);
    return null;
});
//# sourceMappingURL=UserEditPlugin.js.map